/**
 * The customer knowledge base: what the bot answers general questions from.
 *
 * ## Why the test box is the most important control here
 *
 * A document that is indexed but does not match how customers ask is the way a knowledge base
 * quietly fails. Everything on the list says `indexed`, and the bot still refuses — because the
 * fee table is titled "Tariff schedule 2026" and customers ask "qaddesh rusum el tahwil". Nothing
 * on an upload screen can tell you that.
 *
 * So the question box runs the **same call the bot makes** and shows its verdict first: `ok` or
 * `no_grounding`. `no_grounding` means a customer asking this gets a refusal, and that is the
 * thing worth knowing before anyone reports the bot as broken.
 *
 * ## Withdraw and delete are different buttons because they are different decisions
 *
 * Withdrawing retires the passages: the bot stops using them on the next question, and the row
 * stays so an audit entry citing a chunk still resolves. It is also usually temporary — a fee
 * schedule pulled while a correction is agreed — so it is a toggle rather than a confirmation.
 *
 * Deleting is "this should not be here": the wrong file, a duplicate, a fixture that reached the
 * live collection. It takes the row off the list as well, so it asks first and names the document
 * in the question. Underneath it is the same soft retirement — the row survives and old citations
 * still resolve — which is why the confirmation can say what it does without hedging.
 *
 * ## Converting is how anybody sees the text before a customer does
 *
 * Between an upload and an answer there is no step where the extracted text is visible, and this
 * knowledge base's two worst defects lived in exactly that gap: a fee table truncated mid-answer,
 * and a document whose Arabic was read off the page backwards and quoted to trial users as
 * nonsense. The converter runs the same parsers an upload runs and hands back what they produced,
 * to read, correct and upload again.
 *
 * ## No draft and no approval step
 *
 * Unlike every other screen in this dashboard. The reasoning is in `customer_admin/api/kb.py`: an
 * outcome message is a promise about what the bank will say and belongs in a version somebody
 * approves; a knowledge document is a source the bot quotes with a citation, and a correction
 * should reach customers on the next question rather than wait for a config release.
 */

import {
  ApiError,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Pager,
  Select,
  StatusPill,
  Table,
  directionOfText,
  useToast,
  type Column,
  type StatusTone,
} from "@nab/shared";

import {
  askKb,
  convertKbDocument,
  deleteKbDocument,
  fetchCustomerKbDocuments,
  fetchKbLimits,
  setKbPublished,
  uploadCustomerKbDocument,
  type CustomerKbAnswer,
  type CustomerKbDocument,
  type CustomerKbLimits,
} from "../kbApi";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** Rows per page. The API's own default. */
const PAGE_SIZE = 50;

/** Indexing status to a meaning. `uploaded` is neutral: in progress is not wrong. */
const STATUS_TONE: Record<string, StatusTone> = {
  indexed: "success",
  uploaded: "neutral",
  indexing: "neutral",
  failed: "danger",
  // Shown only on the row the server hands back from a delete, since the listing drops them.
  deleted: "danger",
};

const EMPTY_FORM = {
  doc_id: "",
  title: "",
  version: "1.0",
  language: "ar",
  topic: "",
  effective_date: "",
  valid_to: "",
};

export function KnowledgePage(): JSX.Element {
  const { t } = useTranslation("app");
  const toast = useToast();

  const [documents, setDocuments] = useState<CustomerKbDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState<CustomerKbLimits | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [uploading, setUploading] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<CustomerKbDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [convertFile, setConvertFile] = useState<File | null>(null);
  const [convertFormat, setConvertFormat] = useState("md");
  const [converting, setConverting] = useState(false);

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<CustomerKbAnswer | null>(null);
  const [asking, setAsking] = useState(false);

  const term = search.trim();

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const page = await fetchCustomerKbDocuments({
        limit: PAGE_SIZE,
        offset,
        ...(term ? { q: term } : {}),
      });
      setDocuments(page.documents);
      setTotal(page.total);
    } catch (caught) {
      toast.show({
        tone: "error",
        message: t("kb.loadFailed"),
        ...(caught instanceof ApiError && typeof caught.detail === "string"
          ? { detail: caught.detail }
          : {}),
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, term, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // A new search goes back to the first page: asking for rows 150-200 of a three-row result set
  // renders empty, which reads as "no such document" rather than "wrong page".
  useEffect(() => {
    setOffset(0);
  }, [term]);

  useEffect(() => {
    void fetchKbLimits().then(setLimits).catch(() => setLimits(null));
  }, []);

  const ready = Boolean(file && form.doc_id.trim() && form.title.trim() && form.version.trim());

  async function submit(): Promise<void> {
    if (!file) return;
    setUploading(true);
    try {
      const saved = await uploadCustomerKbDocument(file, form);
      toast.show({
        tone: "success",
        message: t("kb.uploaded", { title: saved.title }),
        detail: t("kb.uploadedDetail", { count: saved.indexedChunks }),
      });
      setFile(null);
      setForm({ ...EMPTY_FORM });
      await reload();
    } catch (caught) {
      toast.show({
        tone: "error",
        message: t("kb.uploadFailed"),
        ...(caught instanceof ApiError && typeof caught.detail === "string"
          ? { detail: caught.detail }
          : {}),
      });
    } finally {
      setUploading(false);
    }
  }

  async function toggle(row: CustomerKbDocument): Promise<void> {
    try {
      await setKbPublished(row.rowId, !row.published);
      await reload();
    } catch (caught) {
      toast.show({
        tone: "error",
        message: t("kb.withdrawFailed"),
        ...(caught instanceof ApiError && typeof caught.detail === "string"
          ? { detail: caught.detail }
          : {}),
      });
    }
  }

  async function remove(): Promise<void> {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteKbDocument(pendingDelete.rowId);
      toast.show({
        tone: "success",
        message: t("kb.delete.done", { title: pendingDelete.title }),
      });
      setPendingDelete(null);
      await reload();
    } catch (caught) {
      toast.show({
        tone: "error",
        message: t("kb.delete.failed"),
        ...(caught instanceof ApiError && typeof caught.detail === "string"
          ? { detail: caught.detail }
          : {}),
      });
    } finally {
      setDeleting(false);
    }
  }

  async function convert(): Promise<void> {
    if (!convertFile) return;
    setConverting(true);
    try {
      const { text, filename } = await convertKbDocument(convertFile, convertFormat, form);
      // Saved rather than shown. The point is to open it in an editor, correct it and upload it
      // back, and a preview pane would be a worse editor than the one they already have.
      const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.show({ tone: "success", message: t("kb.convert.done", { filename }) });
    } catch (caught) {
      toast.show({
        tone: "error",
        message: t("kb.convert.failed"),
        ...(caught instanceof ApiError && typeof caught.detail === "string"
          ? { detail: caught.detail }
          : {}),
      });
    } finally {
      setConverting(false);
    }
  }

  async function ask(): Promise<void> {
    if (!question.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      setAnswer(await askKb(question.trim()));
    } catch (caught) {
      toast.show({
        tone: "error",
        message: t("kb.askFailed"),
        ...(caught instanceof ApiError && typeof caught.detail === "string"
          ? { detail: caught.detail }
          : {}),
      });
    } finally {
      setAsking(false);
    }
  }

  const columns: Column<CustomerKbDocument>[] = [
    {
      key: "title",
      header: t("kb.column.title"),
      render: (row) => (
        <div className="flex flex-col">
          <span dir={directionOfText(row.title)} className="text-body-md text-textPrimary">
            {row.title}
          </span>
          <span className="text-label-md text-textMuted">
            {row.docId} · v{row.version} · {row.language}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      header: t("kb.column.status"),
      render: (row) => (
        <div className="flex flex-col gap-1">
          <StatusPill tone={STATUS_TONE[row.status] ?? "neutral"}>
            {t(`kb.status.${row.status}`, { defaultValue: row.status })}
          </StatusPill>
          {row.statusDetail ? (
            <span className="text-label-md text-statusDanger">{row.statusDetail}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "chunks",
      header: t("kb.column.passages"),
      // The count and what it counts, together. "40" means one thing for a file read as 40
      // questions and quite another for one read as 40 paragraphs, and the second is how an FAQ
      // that was not recognised looks from here.
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-body-md">{row.indexedChunks}</span>
          <span className="text-label-md text-textMuted">
            {t(row.kind === "qa" ? "kb.kind.qa" : "kb.kind.prose")}
          </span>
        </div>
      ),
    },
    {
      key: "live",
      header: t("kb.column.live"),
      render: (row) => (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => void toggle(row)}>
            {row.published ? t("kb.withdraw") : t("kb.restore")}
          </Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
            {t("kb.delete.action")}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-6 p-6" aria-label={t("kb.heading")}>
      <Card>
        <h2 className="text-title-sm text-textPrimary">{t("kb.ask.heading")}</h2>
        <p className="mt-1 text-body-sm text-textSecondary">{t("kb.ask.hint")}</p>
        <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-end">
          <div className="flex-1">
            <Input
              label={t("kb.ask.label")}
              value={question}
              dir={directionOfText(question)}
              onChange={(event) => setQuestion(event.target.value)}
            />
          </div>
          <Button variant="primary" loading={asking} disabled={!question.trim()} onClick={() => void ask()}>
            {t("kb.ask.submit")}
          </Button>
        </div>

        {answer ? (
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <StatusPill tone={answer.status === "ok" ? "success" : "warning"}>
                {t(`kb.ask.status.${answer.status}`, { defaultValue: answer.status })}
              </StatusPill>
              <span className="text-label-md text-textMuted">
                {t("kb.ask.meta", {
                  ms: answer.latencyMs,
                  collections: answer.collectionsSearched.join(", ") || "—",
                })}
              </span>
            </div>
            {answer.status !== "ok" ? (
              <p className="text-body-sm text-textSecondary">{t("kb.ask.refusalMeans")}</p>
            ) : null}
            {answer.passages.map((passage) => (
              <div
                key={`${passage.docId}-${passage.section ?? ""}-${passage.text.slice(0, 24)}`}
                className="rounded-sm border border-borderSubtle p-3"
              >
                <p className="text-label-md text-textMuted">
                  {passage.title}
                  {passage.section ? ` · ${passage.section}` : ""}
                </p>
                <p dir={directionOfText(passage.text)} className="mt-1 text-body-sm text-textPrimary">
                  {passage.text.slice(0, 400)}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </Card>

      <Card>
        <h2 className="text-title-sm text-textPrimary">{t("kb.convert.heading")}</h2>
        <p className="mt-1 text-body-sm text-textSecondary">{t("kb.convert.hint")}</p>

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-label-md text-textPrimary">
            {t("kb.convert.file")}
            <input
              type="file"
              className="rounded-sm border border-border bg-surfaceRaised p-2 text-body-sm"
              accept={limits?.supportedSuffixes.join(",")}
              onChange={(event) => setConvertFile(event.target.files?.[0] ?? null)}
            />
          </label>
          <Select
            label={t("kb.convert.format")}
            value={convertFormat}
            hint={t("kb.convert.formatHint")}
            options={(limits?.exportFormats ?? ["md", "json"]).map((format) => ({
              value: format,
              label: t(`kb.convert.formatName.${format}`, { defaultValue: format }),
            }))}
            onChange={(event) => setConvertFormat(event.target.value)}
          />
        </div>

        <div className="mt-4">
          <Button
            variant="secondary"
            loading={converting}
            disabled={!convertFile}
            onClick={() => void convert()}
          >
            {t("kb.convert.submit")}
          </Button>
        </div>
      </Card>

      <Card>
        <h2 className="text-title-sm text-textPrimary">{t("kb.upload.heading")}</h2>
        <p className="mt-1 text-body-sm text-textSecondary">
          {limits
            ? t("kb.upload.hint", {
                formats: limits.supportedSuffixes.join(", "),
                mb: Math.round(limits.maxBytes / (1024 * 1024)),
              })
            : t("kb.upload.hintPlain")}
        </p>

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-label-md text-textPrimary">
            {t("kb.field.file")}
            <input
              type="file"
              className="rounded-sm border border-border bg-surfaceRaised p-2 text-body-sm"
              accept={limits?.supportedSuffixes.join(",")}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
          <Input
            label={`${t("kb.field.docId")} *`}
            value={form.doc_id}
            hint={t("kb.field.docIdHint")}
            onChange={(event) => setForm({ ...form, doc_id: event.target.value })}
          />
          <Input
            label={`${t("kb.field.title")} *`}
            value={form.title}
            dir={directionOfText(form.title)}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
          <Input
            label={`${t("kb.field.version")} *`}
            value={form.version}
            onChange={(event) => setForm({ ...form, version: event.target.value })}
          />
          <Input
            label={t("kb.field.topic")}
            value={form.topic}
            onChange={(event) => setForm({ ...form, topic: event.target.value })}
          />
          <Input
            label={t("kb.field.effectiveDate")}
            type="date"
            value={form.effective_date}
            onChange={(event) => setForm({ ...form, effective_date: event.target.value })}
          />
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <Button variant="primary" loading={uploading} disabled={!ready} onClick={() => void submit()}>
            {t("kb.upload.submit")}
          </Button>
          {!ready && !uploading ? (
            <p className="text-label-md text-textMuted">{t("kb.upload.needed")}</p>
          ) : null}
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-title-sm text-textPrimary">{t("kb.list.heading")}</h2>
          <div className="w-64">
            <Input
              label={t("kb.list.search")}
              value={search}
              dir={directionOfText(search)}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="mt-3">
          {loading ? (
            <p className="text-body-sm text-textMuted">{t("kb.list.loading")}</p>
          ) : (
            <Table
              caption={t("kb.list.heading")}
              columns={columns}
              rows={documents}
              rowKey={(row) => row.rowId}
              empty={
                <EmptyState
                  title={term ? t("kb.list.noMatches") : t("kb.list.empty")}
                  description={term ? t("kb.list.noMatchesHint") : t("kb.list.emptyHint")}
                />
              }
            />
          )}
        </div>

        <div className="pt-3">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={offset}
            onChange={setOffset}
            busy={loading}
            label={t("kb.pager.label")}
            previousLabel={t("kb.pager.previous")}
            nextLabel={t("kb.pager.next")}
            summary={t("kb.pager.summary", {
              from: offset + 1,
              to: Math.min(offset + PAGE_SIZE, total),
              total,
            })}
          />
        </div>
      </Card>

      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title={t("kb.delete.confirmTitle")}
        closeLabel={t("kb.delete.close")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingDelete(null)}>
              {t("kb.delete.cancel")}
            </Button>
            <Button variant="danger" loading={deleting} onClick={() => void remove()}>
              {t("kb.delete.confirm")}
            </Button>
          </>
        }
      >
        <p dir={directionOfText(pendingDelete?.title ?? "")} className="text-body-md text-textPrimary">
          {t("kb.delete.confirmBody", { title: pendingDelete?.title ?? "" })}
        </p>
        <p className="mt-2 text-body-sm text-textSecondary">
          {t("kb.delete.confirmConsequence")}
        </p>
      </Modal>
    </section>
  );
}
